import i18n from '../i18n';
import { GradeSource } from './Assignment';
import { Company, DefaultOpt } from './Company';
import { Content } from './Content';
import { Cost } from './Cost';
import { CourseSectionAnalytic } from './CourseSectionAnalytic';
import { CourseSectionAverageGrade } from './CourseSectionAverageGrade';
import { CourseSectionEnrollmentTotal } from './CourseSectionEnrollmentTotal';
import { CourseSectionGrade } from './CourseSectionGrade';
import { CourseSectionPrice } from './CourseSectionPrice';
import { Distributor } from './Distributor';
import { Enrollment } from './Enrollment';
import { EnrollmentAverageCompletionTime } from './EnrollmentAverageCompletionTime';
import { Item } from './Item';
import { Module } from './Module';
import { Product } from './Product';
import { PublisherFee } from './PublisherFee';
import { QuestionDifficulty, QuestionFormat } from './Question';
import { QuestionCourseSectionGrade } from './QuestionCourseSectionGrade';
import { Syllabus } from './Syllabus';
import { Term } from './Term';
import { Timezone } from './Timezone';

export enum AvailabilityEndBasis {
  DUE_AT = 'DUE_AT',
  GRADE_AT = 'GRADE_AT',
}

export enum DashboardType {
  CALENDAR = 'CALENDAR',
  AGENDA = 'AGENDA',
}

export enum CourseSectionSource {
  API = 'API',
  MANUAL = 'MANUAL',
  LTI = 'LTI',
}

export enum ModuleLabel {
  MODULE = 'MODULE',
  UNIT = 'UNIT',
}

export interface AssignmentDefaults {
  READING_COMPREHENSION: {
    questionDifficulty: QuestionDifficulty[];
    questionFormats?: QuestionFormat[];
    numberOfQuestions: number;
    availableAtTime: string;
    dueAtTime: string;
    allowedAttempts: number;
    gradeSource: GradeSource;
    syncToGradebook: boolean;
  };
  STUDY_GUIDE: {
    questionDifficulty: QuestionDifficulty[];
    questionFormats?: QuestionFormat[];
    numberOfQuestions: number;
    availableAtTime: string;
    dueAtTime: string;
    allowedAttempts: number;
    gradeSource: GradeSource;
    syncToGradebook: boolean;
  };
  QUIZ: {
    questionDifficulty: QuestionDifficulty[];
    questionFormats?: QuestionFormat[];
    numberOfQuestions: number;
    availableAtTime: string;
    dueAtTime: string;
    allowedAttempts: number;
    timeLimit: number;
    gradeSource: GradeSource;
    syncToGradebook: boolean;
  };
  TEST: {
    questionDifficulty: QuestionDifficulty[];
    questionFormats?: QuestionFormat[];
    numberOfQuestions: number;
    availableAtTime: string;
    dueAtTime: string;
    allowedAttempts: number;
    timeLimit: number;
    gradeSource: GradeSource;
    lockdownBrowser: boolean;
    preventScreenshot: boolean;
    syncToGradebook: boolean;
  };
  DISCUSSION: {
    questionDifficulty: QuestionDifficulty[];
    numberOfQuestions: number;
    points: number;
    replyPercent: number;
    availableAtTime: string;
    dueAtTime: string;
    aiRiskPercent: number;
    plagiarismRiskPercent: number;
    minResponsesCount: number;
    minRepliesCount: number;
    minWordsPerResponse: number;
    minWordsPerReply: number;
    syncToGradebook: boolean;
  };
  TUTOR: {
    questionDifficulty: QuestionDifficulty[];
    availableAtTime: string;
    dueAtTime: string;
    allowedAttempts: number;
    gradeSource: GradeSource;
    syncToGradebook: boolean;
    points: number;
    loCount: number;
    loMinPrompts: number;
    loMaxPrompts: number;
  };
}

export interface CourseSection extends Item {
  termId?: number;
  schoolId?: number;
  name: string;
  courseCode: string;
  departmentName?: string;
  courseNumber?: string;
  sectionNumber?: string;
  lmsId?: string;
  products?: Product[];
  contents?: Content[];
  sisId?: string;
  lmsSectionId?: string;
  sisSectionId?: string;
  sectionName?: string;
  posId?: string;
  ltiId?: string;
  timezone?: Timezone;
  startAt?: Date;
  endAt?: Date;
  hasStudentFeedback: boolean;
  isActive?: boolean;
  deactivatedAt?: Date;
  enrollments: Enrollment[];
  instructors?: Enrollment[];
  moduleLabel?: ModuleLabel;
  costId?: number;
  cost?: Cost;
  publisherFeeId?: number;
  publisherFee?: PublisherFee;

  source: CourseSectionSource;

  isbn: string;
  isCourseware: boolean;
  isGradeSyncEnabled: boolean;
  isProvisioned: boolean;

  isPreview?: boolean;
  code?: string;

  color: string;
  dropDate?: Date;
  price?: CourseSectionPrice;
  hasReading: boolean;
  hasStudy: boolean;
  hasTutor: boolean;
  hasQuiz: boolean;
  hasTest: boolean;
  hasDiscussion: boolean;

  perReading: number;
  perStudy: number;
  perTutor: number;
  perQuiz: number;
  perTest: number;
  perDiscussion: number;

  dashboardType: DashboardType;
  isDisplayGradeAt: boolean;

  // Optional
  term?: Term;
  school?: Company;
  availabilityEndBasis?: AvailabilityEndBasis;
  modules?: Module[];
  syllabus?: Syllabus;
  averageGrade?: CourseSectionAverageGrade;
  questionGrades?: QuestionCourseSectionGrade[];
  grades?: CourseSectionGrade[];

  defaultOpt?: DefaultOpt;

  distributorId?: number;
  distributor?: Distributor;

  studentCount?: number;

  expectedDeliveryAt?: Date;

  averageCompletionTime?: EnrollmentAverageCompletionTime;

  analytic: CourseSectionAnalytic;

  enrollmentTotal: CourseSectionEnrollmentTotal;

  assignmentDefaults: AssignmentDefaults;

  percentOnboard?: number;
  syllabusPercentOnboard?: number;
  pricePercentOnboard?: number;
  estimatedEnrollment?: number;
}

export const isLaunched = (courseSection: CourseSection): boolean => {
  return courseSection.lmsId !== null;
};

export const isAdopted = (courseSection: CourseSection): boolean => {
  return courseSection?.products?.length !== undefined && courseSection?.products?.length > 0;
};

export enum CartType {
  Enrollment = 'Enrollment',
  AccessCodes = 'AccessCodes',
}

export const displayCourseSectionName = (courseSection: CourseSection | undefined) => {
  if (!courseSection) {
    return '-';
  }

  return (
    courseSection.courseCode + (courseSection.sectionName ? ` - ${courseSection.sectionName}` : '')
  );
};

export const displayInstructors = (instructors: Enrollment[]) => {
  if (!instructors) {
    return '';
  }

  if (instructors.length === 1) {
    return `${instructors[0].person?.lastName}`;
  }

  if (instructors.length === 2) {
    return `${instructors[0].person?.lastName} ${i18n.t('and')} ${instructors[1].person?.lastName}`;
  }

  if (instructors.length > 2) {
    return `${instructors[0].person?.lastName} et al.`;
  }
};

export const displayFullglassCoursewareName = (
  courseSection: CourseSection,
  schoolName?: string,
  productTitle?: string,
  instructorLastName?: string
) => {
  let fullglassName = '';
  const { products } = courseSection;
  if (products && products.length > 0) {
    fullglassName = products[0].bookTitle + '-';
  } else if (productTitle) {
    fullglassName = productTitle + '-';
  }

  fullglassName += (courseSection?.school?.name ?? schoolName) + '-';
  if (courseSection?.instructors && courseSection.instructors.length > 0) {
    fullglassName += courseSection?.instructors[0]?.person?.lastName + '-';
  } else if (instructorLastName) {
    fullglassName += instructorLastName + '-';
  }
  fullglassName += 'Fullglass Courseware';

  return fullglassName;
};
